/**
 * IPv4 CIDR rules for the address ranges of a Dedicated network (contract
 * revision 4, R4-A), the same as the backend's constants.cidr_problem():
 *  - strict a.b.c.d/nn: no spaces, no zero-padded octet, prefix at most /32;
 *  - canonical form: host bits zero (10.20.0.0/24, not 10.20.0.5/24) and no
 *    zero-padded prefix (/8, not /08), else "Use the network address <canonical>";
 *  - private ranges only: 10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16;
 *  - prefix /16 to /29, for the VNet and the subnet alike;
 *  - the subnet inside the VNet (equal is inside).
 * The checks run in that order and report the first problem. Values are taken
 * as they are; the form trims what the user typed before checking and sending.
 * Overlap with the other WorkWise networks of the environment is a server-side
 * rule; the mock applies it with cidrsOverlap().
 *
 * Pure and Node-safe (the mock layer and the payload schema use it): no imports.
 */

export const CIDR_PREFIX_MIN = 16
export const CIDR_PREFIX_MAX = 29

export const CIDR_MESSAGES = {
  format: 'Enter a CIDR range like 10.20.0.0/24',
  /** `network` is the canonical form of what was typed, e.g. 10.20.0.0/24 for 10.20.0.5/24. */
  canonical: (network: string) => `Use the network address ${network}`,
  private: 'Must be a private range (10.x, 172.16–31.x or 192.168.x)',
  prefix: `Prefix must be between /${CIDR_PREFIX_MIN} and /${CIDR_PREFIX_MAX}`,
  inside: 'The subnet must be inside the VNet range',
} as const

export interface ParsedCidr {
  /** The address as typed, as an unsigned 32-bit number (host bits may be set). */
  address: number
  prefix: number
  /** The prefix was typed with a leading zero (/08): not canonical. */
  paddedPrefix: boolean
}

/** Octets 0-255 without leading zeros; a prefix of one or two digits (0-32 checked below). */
const OCTET = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)'
const CIDR_REGEX = new RegExp(`^${OCTET}\\.${OCTET}\\.${OCTET}\\.${OCTET}/(\\d{1,2})$`)

function maskOf(prefix: number): number {
  return prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0
}

function networkOf(cidr: ParsedCidr): number {
  return (cidr.address & maskOf(cidr.prefix)) >>> 0
}

function formatAddress(value: number): string {
  return [value >>> 24, (value >>> 16) & 255, (value >>> 8) & 255, value & 255].join('.')
}

/** Strict a.b.c.d/nn, or null when malformed. Host bits and a padded prefix are allowed here (see cidrProblem). */
export function parseCidr(value: string): ParsedCidr | null {
  const match = CIDR_REGEX.exec(value)
  if (!match) return null
  const [a, b, c, d, prefix] = match.slice(1).map(Number)
  if (prefix > 32) return null
  return { address: ((a << 24) >>> 0) + (b << 16) + (c << 8) + d, prefix, paddedPrefix: match[5].length === 2 && match[5][0] === '0' }
}

const PRIVATE_RANGES = ['10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16'].map((range) => parseCidr(range) as ParsedCidr)

/** True when `inner` lies entirely inside `outer`. */
function inside(outer: ParsedCidr, inner: ParsedCidr): boolean {
  return inner.prefix >= outer.prefix && ((inner.address & maskOf(outer.prefix)) >>> 0) === networkOf(outer)
}

/** The first problem with one range, or null when it is valid. */
export function cidrProblem(value: string): string | null {
  const cidr = parseCidr(value)
  if (!cidr) return CIDR_MESSAGES.format
  const network = networkOf(cidr)
  if (network !== cidr.address || cidr.paddedPrefix) return CIDR_MESSAGES.canonical(`${formatAddress(network)}/${cidr.prefix}`)
  if (!PRIVATE_RANGES.some((range) => inside(range, cidr))) return CIDR_MESSAGES.private
  if (cidr.prefix < CIDR_PREFIX_MIN || cidr.prefix > CIDR_PREFIX_MAX) return CIDR_MESSAGES.prefix
  return null
}

export type CidrField = 'vnetCidr' | 'subnetCidr'

/** The problems of a VNet + subnet pair by field: each range on its own, then the subnet inside the VNet. */
export function cidrPairProblems(vnetCidr: string, subnetCidr: string): Partial<Record<CidrField, string>> {
  const problems: Partial<Record<CidrField, string>> = {}
  const vnet = cidrProblem(vnetCidr)
  const subnet = cidrProblem(subnetCidr)
  if (vnet) problems.vnetCidr = vnet
  if (subnet) problems.subnetCidr = subnet
  if (!vnet && !subnet && !cidrContains(vnetCidr, subnetCidr)) problems.subnetCidr = CIDR_MESSAGES.inside
  return problems
}

/** True when the range `inner` lies inside `outer`; false when either is malformed. */
export function cidrContains(outer: string, inner: string): boolean {
  const a = parseCidr(outer.trim())
  const b = parseCidr(inner.trim())
  return a !== null && b !== null && inside(a, b)
}

/** True when two ranges share any address; false when either is malformed. */
export function cidrsOverlap(first: string, second: string): boolean {
  const a = parseCidr(first.trim())
  const b = parseCidr(second.trim())
  if (!a || !b) return false
  const mask = maskOf(Math.min(a.prefix, b.prefix))
  return ((a.address & mask) >>> 0) === ((b.address & mask) >>> 0)
}
